import { useEffect } from 'react';
import { rootStore } from './store-root';
import { runInAction } from 'mobx';
import { CmdError } from '@smart-tv/contexts/error-context/error-context';
import { KMMError } from '@norlys-a-s/norlys-play-kmm';

interface UseStripesProviderStoreParams {
  providerId: number;
  universeId?: number;
}

/**
 * Handles successful data fetching
 */
const handleFetchSuccess = async (universeId?: number) => {
  const store = rootStore.stripesProviderStore;

  // If we have a universeId, fetch the genres for that universe
  if (universeId) {
    await store.fetchGenres();
  }

  runInAction(() => {
    store.setLoading(false);
  });
};

/**
 * Handles errors during data fetching
 */
const handleFetchError = (error: unknown) => {
  const store = rootStore.stripesProviderStore;

  runInAction(() => {
    if (error instanceof KMMError.ApiError) {
      const err = new CmdError(parseInt(error.code), error.message);
      store.setError(err);
    } else {
      store.setError(error as CmdError);
    }
    store.setLoading(false);
  });
};

/**
 * Fetches all required data for the provider store
 */
const fetchProviderData = async (providerId: number, universeId?: number) => {
  const store = rootStore.stripesProviderStore;

  try {
    await Promise.all([store.fetchUiElements(), store.fetchNavigationContent()]);
    await handleFetchSuccess(universeId);
  } catch (error) {
    handleFetchError(error);
  }
};

const useStripesProviderStore = ({ providerId, universeId }: UseStripesProviderStoreParams) => {
  useEffect(() => {
    const store = rootStore.stripesProviderStore;
    store.resetStore();
    store.setParams({ providerId, universeId });
    store.setSelectedUniverseId(universeId);

    fetchProviderData(providerId, universeId);

    return () => {
      store.resetStore();
      store.setError(null);
    };
  }, [providerId, universeId]);

  return rootStore.stripesProviderStore;
};

export default useStripesProviderStore;
